import { Box } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

/**
 * Thin top loading bar shown during client-side route changes — gives the
 * immediate feedback that makes navigation feel like a native app rather than
 * a web page that "just sits there" while the next page/config loads.
 */
export default function RouteProgressBar() {
  const router = useRouter();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearTimers = () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    };

    const start = () => {
      clearTimers();
      setVisible(true);
      setProgress(12);
      // Ease towards 90% while the next page resolves.
      tickRef.current = setInterval(() => {
        setProgress((p) => (p < 90 ? p + (90 - p) * 0.12 : p));
      }, 180);
    };

    const done = () => {
      clearTimers();
      setProgress(100);
      hideRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 250);
    };

    router.events.on("routeChangeStart", start);
    router.events.on("routeChangeComplete", done);
    router.events.on("routeChangeError", done);

    return () => {
      clearTimers();
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", done);
      router.events.off("routeChangeError", done);
    };
  }, [router]);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      h="3px"
      zIndex={2000}
      pointerEvents="none"
      opacity={visible ? 1 : 0}
      transition="opacity 0.25s ease"
    >
      <Box
        h="100%"
        w={`${progress}%`}
        bg="primary.500"
        borderRightRadius="full"
        transition="width 0.18s ease-out"
        boxShadow="0 0 10px var(--chakra-colors-primary-400, currentColor)"
      />
    </Box>
  );
}
